import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { JobsPanelState, JobsStatus, jobStatusToString } from "../../../constants/Jobs";
import { JobContext, UserContext, VersionContext } from "../../../Context";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import { createNewJobStep, getJobRelatedObjects, updateJobStepComment } from "../../../utils/jobsUtils";
import InputField from "../../genericComponents/InputField/InputField";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./JobDetails.css";

import ApproveIcon from "../../../assets/jobs/approve.svg?react";
import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";
import RejectIcon from "../../../assets/jobs/reject.svg?react";

import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import { isDefaultVersion } from "../../../utils/versioningUtils";
import ConfirmationPopover from "../../genericComponents/confirmationPopover/confirmationPopover";
import { FeatureWithChanges, findFeaturesWithChangesFromJob } from "../../../utils/newVersioningUtils";
import EditedFeaturesForOperativeCompare from "../EditedFeatures/EditedFeaturesForOperativeCompare";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobQA1DetailsProps {
    jobObject: Graphic;
    setJobPanelState: (state: JobsPanelState) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobQA1Details: React.FC<JobQA1DetailsProps> = ({
    jobObject,
    setJobPanelState,
    setLoading,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const [currentJobStep, setCurrentJobStep] = useState<Graphic | null>(null);
    const [previousJobStep, setPreviousJobStep] = useState<Graphic | null>(null);
    const [historicalSteps, setHistoricalSteps] = useState<Graphic[]>([]);

    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);

    const [comment, setComment] = useState<string | undefined>(undefined);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const versionContext = useContext(VersionContext);
    const versionManagementRef = versionContext?.versionServiceRef.value;

    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const jobsContext = useContext(JobContext);

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const checkIfDefaultVersion = () => {
        if (versionManagementRef?.current && activeVersion) {
            return isDefaultVersion(activeVersion, versionManagementRef.current);
        }
    };

    const approveQA1Job = async () => {
        setLoading(true);
        if (jobObject) {
            const success = await updateJobStepComment(
                currentJobStep?.attributes.globalid,
                comment ?? "",
                user?.username ?? ""
            );
            if (success) {
                const jobStepGlobalId = await createNewJobStep(jobObject.attributes.globalid, JobsStatus.TilUtføring);
                if (jobStepGlobalId) {
                    // props.resetJobsData();

                    toast.success("Jobben er godkjent og sendt til utføring");
                    dispatch(triggerAlertsRefresh());
                    returnToJobList();
                }
            }
        }
        setLoading(false);
    };

    const rejectQA1Job = async () => {
        setLoading(true);
        if (jobObject) {
            const success = await updateJobStepComment(
                currentJobStep?.attributes.globalid,
                comment ?? "",
                user?.username ?? ""
            );
            if (success) {
                const jobStepGlobalId = await createNewJobStep(
                    jobObject.attributes.globalid,
                    JobsStatus.PlanIkkeGodkjent
                );
                if (jobStepGlobalId) {
                    // props.resetJobsData();
                    toast.info("Jobben er sendt tilbake til planlegger");
                    returnToJobList();
                    dispatch(triggerAlertsRefresh());
                }
            }
        }
        setLoading(false);
    };

    const canIReview = () => {
        if (
            [
                JobsStatus.Ferdig,
                JobsStatus.Utføres,
                JobsStatus.Utført,
                JobsStatus.TilUtføring,
                JobsStatus.TilKontroll,
                JobsStatus.Ukjent,
            ].includes(jobObject.attributes.status)
        ) {
            return false;
        }

        if (jobObject.attributes.status === JobsStatus.QA1 && checkIfDefaultVersion()) {
            if (jobObject.attributes.opprettet_av === user?.username) {
                return false;
            } else {
                return true;
            }
        } else {
            return false;
        }
    };

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;

        if (job && versionName) {
            setIsLoadingChanges(true);
            const objects = await findFeaturesWithChangesFromJob(job, versionName);
            setJobFeaturesWithChanges(objects);
            console.log("Objects with changes from job:", objects);
            setIsLoadingChanges(false);
        }
    };

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);
            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setCurrentJobStep(activeStep);
                // Find step with highest opprettet_dato less than or equal to activeStep
                // but with a different status (could be same timestamp but different status)
                const maxTime = activeStep.attributes.opprettet_dato;
                let previousStep = null;

                // Sort steps by opprettet_dato in descending order

                for (let step of orderedSteps) {
                    if (
                        step.attributes.globalid !== activeStep.attributes.globalid &&
                        step.attributes.opprettet_dato <= maxTime &&
                        step.attributes.jobb_status !== activeStep.attributes.jobb_status
                    ) {
                        previousStep = step;
                        break;
                    }
                }
                setPreviousJobStep(previousStep);

                if (activeStep) {
                    setHistoricalSteps(() => {
                        if (jobObject.attributes.status === JobsStatus.Ferdig) {
                            return orderedSteps;
                        } else {
                            return orderedSteps.filter(
                                (step) => step.attributes.globalid !== activeStep.attributes.globalid
                            );
                        }
                    });
                }
            }
        });
    }, [jobObject]);

    useEffect(() => {
        getObjectsConnectedToJob();
    }, []);

    return (
        <>
            <div className="job-details-body">
                <div className="job-details-body-content">
                    <label className="job-details-label">
                        <span className="hidden">{jobObject.attributes.globalid}</span>
                        <span>Jobbnavn</span>
                        <span>{jobObject.attributes.navn}</span>
                    </label>
                    <label className="job-details-label">
                        <span>Beskrivelse</span>
                        <span>{jobObject.attributes.beskrivelse}</span>
                    </label>
                    {previousJobStep && (
                        <label className="job-details-label">
                            <span>Kommentar</span>
                            <span>{previousJobStep.attributes.kommentar}</span>
                        </label>
                    )}
                    {canIReview() && (
                        <InputField
                            label="Tilbakemelding / kommentar"
                            onChange={setComment}
                            inputFieldId={jobObject.attributes.globalid + "comment"}
                            value={comment}
                            type="text"
                            isTextArea
                            textAreaRows={3}
                            maxLength={255}
                        />
                    )}

                    {historicalSteps.length > 0 && (
                        <Collapsible
                            isOpen={true}
                            setIsOpen={() => {}}
                            title={`Steg med kommentarer (${historicalSteps.length})`}
                            titleClassName="input-field-label"
                        >
                            {historicalSteps.map((step) => (
                                <label key={step.attributes.globalid} className="job-step-container">
                                    <div className="job-step-header">
                                        <span>
                                            <strong>{jobStatusToString(step.attributes.jobb_status)}</strong>
                                        </span>
                                        <span>
                                            {step.attributes.fullfort_av} ({formatDate(step.attributes.oppdatert_dato)})
                                        </span>
                                    </div>
                                    <span>{step.attributes.kommentar ?? "[ingen kommentar]"}</span>
                                </label>
                            ))}
                        </Collapsible>
                    )}

                    <div className="empty-button flex-row small-gap center">
                        <CheckPinIcon />
                        {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Eier</span>
                        <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Oppdatert</span>
                        <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                    </div>
                </div>
                <EditedFeaturesForOperativeCompare
                    featuresAddedToJob={jobFeaturesWithChanges}
                    isLoadingChanges={isLoadingChanges}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                    jobStatus={jobObject.attributes.status}
                />
            </div>
            {canIReview() && checkIfDefaultVersion() && jobObject.attributes.status === JobsStatus.QA1 && (
                <div className="job-details-actions">
                    <div className="job-details-actions-buttons">
                        <NfsButton
                            title={
                                !comment || comment.length === 0
                                    ? "Kan ikke avslå uten kommentar"
                                    : "Avslå denne jobben"
                            }
                            outlined
                            disabled={!comment || comment.length === 0}
                            onClick={rejectQA1Job}
                        >
                            <RejectIcon />
                            Avslå
                        </NfsButton>
                        <ConfirmationPopover
                            message="Bekreft at du ønsker å godkjenne jobben"
                            onConfirm={approveQA1Job}
                            title="Godkjenn jobb"
                            confirmText="Bekreft"
                            cancelText="Avbryt"
                            onCancel={() => {}}
                            buttonStyle={{ width: "50%" }}
                            disabled={isLoadingChanges}
                        >
                            <div className="nfs-button">
                                <ApproveIcon />
                                Godta
                            </div>
                        </ConfirmationPopover>
                    </div>
                </div>
            )}
            {!checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    <span>For å sikre datakvalitet må jobben godkjennes fra Planroot</span>
                </div>
            )}
        </>
    );
};

export default JobQA1Details;
